import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';

const BASE_PATH = '/api/core/pleri/agent-skills';

export type AgentSkillScope = 'user' | 'tenant' | 'template';

export interface AgentSkillFile {
  path: string;
  sizeBytes: number;
  contentType: string;
}

export interface AgentSkill {
  id: string;
  tenantId: string;
  organizationId: string;
  createdBy: string;
  scope: AgentSkillScope;
  name: string;
  displayName: string;
  description: string;
  /** Workspaces this skill applies to. Empty = all workspaces. */
  workspace?: string[];
  instructions: string;
  isBuiltIn: boolean;
  enabled: boolean;
  createdAt: string;
  lastUpdated: string;
  /** Agent Skills spec fields */
  license?: string;
  compatibility?: string;
  metadata?: Record<string, string>;
  tags?: string[];
  allowedTools?: string[];
  files?: AgentSkillFile[];
  /** Category of the skill. */
  skillCategory?: 'playbook' | 'integration' | 'built-in';
  /** Tool group this skill belongs to. */
  toolGroup?: string;
  /** Display name of the skill creator (populated by server for templates). */
  creatorName?: string;
  /** Number of times this template has been forked (templates only). */
  forkCount?: number;
}

export interface CreateAgentSkillInput {
  name: string;
  displayName: string;
  description: string;
  workspace?: string[];
  instructions: string;
  scope?: AgentSkillScope;
  enabled?: boolean;
  license?: string;
  compatibility?: string;
  metadata?: Record<string, string>;
  tags?: string[];
  allowedTools?: string[];
}

export interface UpdateAgentSkillInput {
  displayName?: string;
  description?: string;
  workspace?: string[];
  instructions?: string;
  scope?: AgentSkillScope;
  enabled?: boolean;
  license?: string;
  compatibility?: string;
  metadata?: Record<string, string>;
  tags?: string[];
  allowedTools?: string[];
}

export const agentSkillKeys = {
  all: ['agent-skills'] as const,
  list: () => [...agentSkillKeys.all, 'list'] as const,
};

export const useListAgentSkills = () => {
  return useQuery({
    queryKey: agentSkillKeys.list(),
    queryFn: async () => {
      const { data } = await api.get<{ data: AgentSkill[] }>(BASE_PATH);
      return data.data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  });
};

export const useCreateAgentSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAgentSkillInput) => {
      const { data } = await api.post<{ data: AgentSkill }>(
        `${BASE_PATH}/${input.scope}`,
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

export const useUpdateAgentSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      name,
      ...input
    }: UpdateAgentSkillInput & { scope: AgentSkillScope; name: string }) => {
      const { data } = await api.put<{ data: AgentSkill }>(
        `${BASE_PATH}/${scope}/${name}`,
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

export const useDeleteAgentSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      name,
    }: {
      scope: AgentSkillScope;
      name: string;
    }) => {
      await api.delete(`${BASE_PATH}/${scope}/${name}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

/** Import a skill from a zip file (Agent Skills spec format). */
export const useImportAgentSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      zipFile,
      nameOverride,
      scope,
    }: {
      zipFile: File;
      nameOverride?: string;
      scope?: 'user' | 'tenant' | 'template';
    }) => {
      const buffer = await zipFile.arrayBuffer();
      const zipBase64 = btoa(
        new Uint8Array(buffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          '',
        ),
      );
      const { data } = await api.post<{
        data: AgentSkill;
        message: string;
        warning?: string;
        shadowsOrgSkill?: boolean;
        nameChanged?: boolean;
        originalName?: string;
        assignedName?: string;
      }>(`${BASE_PATH}/import`, {
        zipBase64,
        ...(nameOverride ? { name: nameOverride } : {}),
        ...(scope ? { scope } : {}),
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

/** List files attached to a skill. */
export const useListSkillFiles = (
  skill: { scope: AgentSkillScope; name: string } | null,
) => {
  return useQuery({
    queryKey: [...agentSkillKeys.all, 'files', skill?.name ?? 'none'] as const,
    queryFn: async () => {
      const { data } = await api.get<{ data: AgentSkillFile[] }>(
        skill ? `${BASE_PATH}/${skill.scope}/${skill.name}/files` : '',
      );
      return data.data;
    },
    enabled: !!skill,
  });
};

/** Get a single file's content from a skill. */
export const getSkillFileContent = async (
  skill: { scope: AgentSkillScope; name: string },
  filePath: string,
): Promise<{ content: string; contentType: string; path: string }> => {
  const { data } = await api.get<{
    data: { content: string; contentType: string; path: string };
  }>(
    `${BASE_PATH}/${skill.scope}/${skill.name}/file/${encodeURIComponent(filePath)}`,
  );
  return data.data;
};

/** Upload (add/replace) a single file to an existing skill. */
export const useUploadSkillFile = (skill: {
  scope: AgentSkillScope;
  name: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      path,
      content,
    }: {
      path: string;
      content: string;
    }) => {
      const { data } = await api.post<{
        data: AgentSkillFile;
        message: string;
      }>(`${BASE_PATH}/${skill.scope}/${skill.name}/files`, { path, content });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

export const disabledSkillsKeys = {
  all: ['disabled-skills'] as const,
  user: () => [...disabledSkillsKeys.all, 'user'] as const,
};

export const useDisabledSkills = () => {
  return useQuery({
    queryKey: disabledSkillsKeys.user(),
    queryFn: async () => {
      try {
        const { data } = await api.get<{
          data: { data: { skillIds: string[] } };
        }>('/api/core/pleri/preferences/user/disabled-skills');
        return data.data?.data?.skillIds ?? [];
      } catch {
        return [];
      }
    },
  });
};

export const useToggleSkillDisabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (skillIds: string[]) => {
      await api.put('/api/core/pleri/preferences/user/disabled-skills', {
        skillIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: disabledSkillsKeys.all });
    },
  });
};

/** Delete a single file from a skill. */
export const useDeleteSkillFile = (skill: {
  scope: AgentSkillScope;
  name: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filePath: string) => {
      await api.delete(
        `${BASE_PATH}/${skill.scope}/${skill.name}/file/${encodeURIComponent(filePath)}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Skill templates
// ---------------------------------------------------------------------------

export interface SkillTemplate {
  id: string;
  name: string;
  displayName: string;
  description: string;
  instructions: string;
  category: 'security' | 'compliance' | 'reporting' | 'devops' | 'general';
  workspace?: string[];
  tags?: string[];
  source?: 'builtin' | 'github' | 's3';
  githubOwner?: string;
  githubRepo?: string;
  githubPath?: string;
}

/** List available skill templates. */
export const useListSkillTemplates = () => {
  return useQuery({
    queryKey: [...agentSkillKeys.all, 'templates'] as const,
    queryFn: async () => {
      try {
        const { data } = await api.get<{
          data: { templates: SkillTemplate[] };
        }>(`${BASE_PATH}/templates`);
        return data.data?.templates ?? [];
      } catch {
        return [];
      }
    },
  });
};

/** Install a skill from a template. */
export const useInstallTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      scope = 'user',
    }: {
      templateId: string;
      scope?: 'user' | 'tenant';
    }) => {
      const { data } = await api.post<{ data: AgentSkill }>(
        `${BASE_PATH}/install-template`,
        { templateId, scope },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

/** Fork a template skill into the current user's personal skills. */
export const useForkTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      skill,
      newDisplayName,
      newName,
    }: {
      skill: { scope: AgentSkillScope; name: string };
      newDisplayName?: string;
      /** Optional slug override — prevents conflicts when forking the same template multiple times. */
      newName?: string;
    }) => {
      const { data } = await api.post<{ data: AgentSkill }>(
        `${BASE_PATH}/${skill.scope}/${skill.name}/fork`,
        { newDisplayName, ...(newName ? { name: newName } : {}) },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : 'Failed to fork template',
      );
    },
  });
};

/** Import a skill from a URL (GitHub repo URL or zip URL). */
export const useImportFromUrl = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      url,
      nameOverride,
      scope,
    }: {
      url: string;
      nameOverride?: string;
      scope?: 'user' | 'tenant' | 'template';
    }) => {
      const { data } = await api.post<{
        data: AgentSkill;
        message: string;
        warning?: string;
        shadowsOrgSkill?: boolean;
        nameChanged?: boolean;
        originalName?: string;
        assignedName?: string;
      }>(`${BASE_PATH}/import-from-url`, {
        url,
        ...(nameOverride ? { name: nameOverride } : {}),
        ...(scope ? { scope } : {}),
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentSkillKeys.all,
        refetchType: 'all',
      });
    },
  });
};

/** Export a skill — returns a presigned download URL. */
export const useExportSkill = () => {
  return useMutation({
    mutationFn: async ({
      skill,
    }: {
      skill: { scope: AgentSkillScope; name: string };
    }) => {
      const { data } = await api.get<{ data: { downloadUrl: string } }>(
        `${BASE_PATH}/${skill.scope}/${skill.name}/export`,
      );
      return data.data;
    },
  });
};

/** Get a presigned upload URL for a skill file. */
export const useGetSkillFilePresignedUrl = () => {
  return useMutation({
    mutationFn: async ({
      skill,
      fileName,
      contentType,
    }: {
      skill: { scope: AgentSkillScope; name: string };
      fileName: string;
      contentType: string;
    }) => {
      const { data } = await api.post<{
        data: { uploadUrl: string; path: string };
      }>(`${BASE_PATH}/${skill.scope}/${skill.name}/files/presigned-url`, {
        fileName,
        contentType,
      });
      return data.data;
    },
  });
};
